import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { 
  MessageCircle, 
  Send, 
  Loader2,
  Inbox,
  User,
  Clock,
  CheckCheck,
  ExternalLink,
  RefreshCw,
  ArrowLeft
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSupportChat = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    // Poll for new conversations every 15 seconds
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchMessages(selectedUserId);
      // Poll for new messages every 5 seconds when chat is open
      const interval = setInterval(() => fetchMessages(selectedUserId), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/support-chat/admin/conversations`);
      setConversations(res.data || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/support-chat/admin/messages/${userId}`);
      setMessages(res.data.messages || []);
      setSelectedChat({
        client: res.data.client,
        case: res.data.case
      });
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (error) {
      toast.error("Error al cargar mensajes");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending || !selectedUserId) return;

    setSending(true);
    try {
      await axios.post(`${API}/support-chat/admin/send/${selectedUserId}`, {
        content: newMessage.trim(),
        case_id: selectedChat?.case?.id
      });
      setNewMessage("");
      await fetchMessages(selectedUserId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="admin-support-chat">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-navy-900">
                Centro de Soporte
              </h1>
              <p className="text-navy-500">
                {totalUnread > 0 ? `${totalUnread} mensaje(s) sin leer` : "Todas las conversaciones al día"}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchConversations}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Conversations List */}
          <div className="col-span-1 bg-white rounded-xl border border-navy-100 overflow-hidden">
            <div className="p-4 border-b border-navy-100 bg-navy-50">
              <h2 className="font-semibold text-navy-900">Conversaciones</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox className="w-12 h-12 text-navy-200 mx-auto mb-3" />
                  <p className="text-navy-500">No hay conversaciones</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.user_id}
                    onClick={() => setSelectedUserId(conv.user_id)}
                    className={`p-4 border-b border-navy-50 cursor-pointer transition-colors ${
                      selectedUserId === conv.user_id
                        ? "bg-emerald-50 border-l-4 border-l-emerald-500"
                        : "hover:bg-navy-50"
                    }`}
                    data-testid={`conversation-${conv.user_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-navy-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-navy-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-navy-900 truncate">
                            {conv.user_name || "Cliente"}
                          </p>
                          <p className="text-xs text-navy-500 truncate">
                            {conv.user_email}
                          </p>
                        </div>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-navy-600 mt-2 truncate">
                      {conv.last_sender_role === "admin" && "Tú: "}
                      {conv.last_message}
                    </p>
                    <p className="text-xs text-navy-400 mt-1">
                      {new Date(conv.last_message_at).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="col-span-2 bg-white rounded-xl border border-navy-100 overflow-hidden flex flex-col">
            {!selectedUserId ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <MessageCircle className="w-16 h-16 text-navy-200 mb-4" />
                <p className="text-navy-500">Selecciona una conversación</p>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-navy-100 bg-navy-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUserId(null)}
                      className="lg:hidden"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900">
                        {selectedChat?.client?.name || "Cliente"}
                      </p>
                      <p className="text-xs text-navy-500">
                        {selectedChat?.client?.email}
                      </p>
                    </div>
                  </div>
                  {selectedChat?.case && (
                    <Link to={`/admin/cases/${selectedChat.case.id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Ver Caso
                      </Button>
                    </Link>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 max-h-[450px]">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <MessageCircle className="w-12 h-12 text-navy-200 mb-3" />
                      <p className="text-navy-500">No hay mensajes</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, index) => (
                        <motion.div
                          key={msg.id || index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.sender_role === "admin" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                              msg.sender_role === "admin"
                                ? "bg-emerald-600 text-white rounded-br-md"
                                : "bg-navy-100 text-navy-900 rounded-bl-md"
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            <div className={`flex items-center gap-1 mt-1 text-xs ${
                              msg.sender_role === "admin" ? "text-emerald-200" : "text-navy-400"
                            }`}>
                              <Clock className="w-3 h-3" />
                              {new Date(msg.created_at).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                              {msg.sender_role === "admin" && msg.read_by_client && (
                                <CheckCheck className="w-3 h-3 ml-1" />
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Input */}
                <div className="p-4 border-t border-navy-100">
                  <div className="flex gap-3">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Escribe tu respuesta..."
                      className="resize-none"
                      rows={2}
                      data-testid="admin-chat-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sending}
                      className="bg-emerald-600 hover:bg-emerald-700 px-6"
                      data-testid="admin-send-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminSupportChat;
